import { Quote } from "../quotes/types";
import { OrderType } from "../orders/types";
import { IAircraftSelect } from "../aircraft/types";
import { IDivision, IJob } from "./types";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { Box, Button, MenuItem } from "@mui/material";
import { Field, Formik, useFormikContext } from "formik";
import { Select, TextField } from "formik-mui";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { axios } from "../api";
import { CancelButton } from "../components/Buttons";
import { handleAPIErrors } from "../utils/FormikErrors";
import { CompanyAutocomplete } from "../companies/FormikCompanyAutocomplete";
import { useConfig } from "../hooks/useConfig";

interface FormikAddJobProps {
  jobConfig: { [key: string]: any } | undefined;
  quote?: Quote & {
    deliver: { id: string; name: string };
  };
  createTypeId2nd?: string | number;
  createInitialDeliver?: {
    id: string | number;
    name: string;
  };
  aircraft?: IAircraftSelect[];
  isAutoCreateJob?: boolean;
  isCreateJob?: boolean;
  setValue: (value: IJob | "") => void;
  onDeliverChange?: (value: { id: string | number; name: string } | null) => void;
  /** Parent calls submitForm() on Issue Part / sheet submit to POST /v6/jobs, then continues its own submit. */
  nestedFormRef?: MutableRefObject<{
    submitForm: () => Promise<IJob>;
  } | null>;
  /** When true, shows Work Order Type (maps to `type_id_2nd`). Hidden by default. */
  showWorkOrderType?: boolean;
}

const normalizeSelectNumber = (value: unknown): number | undefined => {
  if (value === "" || value == null || value === false) {
    return undefined;
  }

  if (value === true) {
    return 1;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const normalizeStatusValue = (value: unknown): string => {
  const numericValue = normalizeSelectNumber(value);
  return numericValue == null ? "" : String(numericValue);
};

const DeliverWatcher = ({
  onDeliverChange,
}: {
  onDeliverChange?: (value: { id: string | number; name: string } | null) => void;
}) => {
  const { values } = useFormikContext<any>();

  useEffect(() => {
    const deliver = values?.deliver;
    if (!onDeliverChange) {
      return;
    }

    if (deliver && deliver.id) {
      onDeliverChange({
        id: deliver.id,
        name: deliver.name ?? "",
      });
      return;
    }

    onDeliverChange(null);
  }, [onDeliverChange, values?.deliver]);

  return null;
};

const CreateInitialDeliverWatcher = ({
  createInitialDeliver,
}: {
  createInitialDeliver?: { id: string | number; name: string };
}) => {
  const { values, setFieldValue } = useFormikContext<any>();

  useEffect(() => {
    if (!createInitialDeliver) {
      return;
    }

    const nextDeliverId = String(createInitialDeliver.id);
    const currentDeliverId = values?.deliver?.id != null ? String(values.deliver.id) : "";
    const currentDeliverName = values?.deliver?.name ?? "";

    if (
      currentDeliverId === nextDeliverId &&
      currentDeliverName === createInitialDeliver.name
    ) {
      return;
    }

    const nextDeliver = {
      id: Number.isNaN(Number(nextDeliverId)) ? nextDeliverId : Number(nextDeliverId),
      name: createInitialDeliver.name,
    };

    setFieldValue("deliver", nextDeliver, false);
    setFieldValue("client_id", nextDeliverId, false);
    setFieldValue("deliver_id", nextDeliverId, false);
    setFieldValue("deliver_to", createInitialDeliver.name, false);
  }, [createInitialDeliver, setFieldValue, values?.deliver]);

  return null;
};

export const FormikAddJob: React.FC<FormikAddJobProps> = ({
  jobConfig,
  quote,
  createTypeId2nd,
  createInitialDeliver,
  isAutoCreateJob,
  isCreateJob,
  setValue,
  onDeliverChange,
  nestedFormRef,
  showWorkOrderType = false,
}) => {
  const isRendered = useRef<boolean>(false);
  const [divisions, setDivisions] = useState<IDivision[]>([]);
  const formRef = useRef<any>();
  const pendingParentSubmitRef = useRef<{
    resolve: (job: IJob) => void;
    reject: (err: unknown) => void;
  } | null>(null);
  const quotesConfig = useConfig("quotes");

  const initialJob = useMemo<IJob & { deliver: any }>(() => {
    const quoteDefaultJobStatus = normalizeStatusValue(quotesConfig?.job_status);
    const initialStatusId =
      quote
        ? quoteDefaultJobStatus 
        : normalizeStatusValue(jobConfig?.default_status);

    const initialDeliver = createInitialDeliver
      ? {
          id: String(createInitialDeliver.id),
          name: createInitialDeliver.name,
        }
      : {
          id: quote?.deliver?.id ?? quote?.supplier_id?.toString() ?? "",
          name: quote?.deliver?.name ?? quote?.supplier_name?.toString() ?? "",
        };

    return {
      account_status: "",
      active: true,
      address_line1: "",
      address_line2: "",
      address_line3: "",
      aircraft_id: "0",
      authorised_by: "0",
      authorised: "0",
      city: "",
      client_id: initialDeliver.id,
      deliver_id: initialDeliver.id,
      company_id: quote?.company_id ?? 0,
      country: "",
      billing_date: "0000-00-00",
      date_due: new Date(),
      deliver_to: initialDeliver.name,
      description: "",
      division: quote?.division_id ?? jobConfig!.default_division,
      exported: "",
      finish_time: "0000-00-00 00:00:00",
      job_condition: "",
      locked_by: "",
      manager_id: 0,
      name: quote?.id ? `From Quote ID ${quote.id}` : "",
      parent_id: "0",
      priority: jobConfig!.default_priority,
      purchase_order: "",
      quote_id: quote?.id ?? 0,
      read_only: "",
      reference: quote?.customer_ref ?? "",
      start_time: "0000-00-00 00:00:00",
      status_id: initialStatusId,
      stock_qty: 0,
      template_id: 0,
      to_stock: 0,
      total: "0.00",
      lines: [],
      type: jobConfig?.default_type,
      warranty_id: 0,
      deliver: {
        id: parseInt(initialDeliver.id) ?? 0,
        name: initialDeliver.name,
      },
      comment: quote?.comment ?? "",
      note: quote?.description ?? "",
      terms: quote?.terms ?? "",
      type_id_2nd: showWorkOrderType ? "2" : "",
    };
  }, [createInitialDeliver, jobConfig, quote, quotesConfig, showWorkOrderType]);

  useEffect(() => {
    isRendered.current = true;

    axios
      .get("/v6/jobs/divisions")
      .then((resp) => {
        if (isRendered.current) {
          setDivisions(resp.data.data);
        }
        return null;
      })
      .catch((error) => {
        enqueueSnackbar(`Error fetching divisions `, {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
      });

    return () => {
      isRendered.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isCreateJob) {
      return;
    }
    let submitted = false;
    const trySubmit = () => {
      if (submitted || !formRef.current) {
        return;
      }
      submitted = true;
      formRef.current.handleSubmit();
    };
    trySubmit();
    const t = window.setTimeout(trySubmit, 0);
    return () => window.clearTimeout(t);
  }, [isCreateJob]);

  useEffect(() => {
    if (!nestedFormRef) {
      return;
    }
    nestedFormRef.current = {
      submitForm: async () => {
        const f = formRef.current;
        if (!f) {
          throw new Error("Work order form is not ready.");
        }
        const validationErrors = await f.validateForm();
        if (validationErrors && Object.keys(validationErrors).length > 0) {
          f.setTouched(
            Object.keys(validationErrors).reduce<Record<string, boolean>>(
              (acc, key) => {
                acc[key] = true;
                return acc;
              },
              {}
            )
          );
          throw new Error("Please complete the new work order fields.");
        }
        return await new Promise<IJob>((resolve, reject) => {
          pendingParentSubmitRef.current = { resolve, reject };
          f.submitForm().catch((err: unknown) => {
            pendingParentSubmitRef.current = null;
            reject(err);
          });
        });
      },
    };
    return () => {
      nestedFormRef.current = null;
    };
  }, [nestedFormRef]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        type_id_2nd: showWorkOrderType
          ? Yup.string()
              .oneOf(["1", "2", "3"], "Select a valid work order type")
              .required("Work order type is required")
          : Yup.string().optional(),
      }),
    [showWorkOrderType]
  );

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialJob}
      validationSchema={validationSchema}
      onSubmit={async (values, { setErrors }) => {
        try {
          const payload = {
            ...values,
            client_id: normalizeSelectNumber(values["deliver"]?.id) ?? 0,
            deliver_id: normalizeSelectNumber(values["deliver"]?.id) ?? 0,
            deliver_to: values["deliver"]?.name ?? "",
            division: normalizeSelectNumber(values["division"]),
            manager_id: normalizeSelectNumber(values["manager_id"]),
            parent_id: normalizeSelectNumber(values["parent_id"]),
            quote_id: normalizeSelectNumber(values["quote_id"]),
            status_id: normalizeSelectNumber(values["status_id"]),
            template_id: normalizeSelectNumber(values["template_id"]),
            to_stock: normalizeSelectNumber(values["to_stock"]) ?? 0,
            type: normalizeSelectNumber(values["type"]),
            type_id_2nd: normalizeSelectNumber(values["type_id_2nd"]) ?? null,
            warranty_id: normalizeSelectNumber(values["warranty_id"]),
          };

          if (payload.type_id_2nd == null && createTypeId2nd != null) {
            payload.type_id_2nd = normalizeSelectNumber(createTypeId2nd) ?? null;
          }

          const resp = await axios.post<IJob>("/v6/jobs", payload);
          !isAutoCreateJob && enqueueSnackbar("Successfully created job", {
            variant: "success",
          });
          setValue(resp.data);
          pendingParentSubmitRef.current?.resolve(resp.data);
          pendingParentSubmitRef.current = null;
        } catch (err) {
          pendingParentSubmitRef.current?.reject(err);
          pendingParentSubmitRef.current = null;
          handleAPIErrors(err, setErrors);
          enqueueSnackbar("Error creating job", {
            variant: "error",
          });
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => (
        // this is intended to be nested in an existing form so we use a div here, forms in forms are invalid html
        <div data-testid="add-job-form">
            <CreateInitialDeliverWatcher createInitialDeliver={createInitialDeliver} />
            <DeliverWatcher onDeliverChange={onDeliverChange} />
            <Field type="hidden" name="client_id" />
            {showWorkOrderType && (
              <>
                <Field
                  component={Select}
                  id="type_id_2nd"
                  name="type_id_2nd"
                  label="Work Order Type"
                  displayEmpty
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: unknown) => {
                      if (selected === "" || selected == null) {
                        return "";
                      }
                      const labels: Record<string, string> = {
                        "1": "Work Order",
                        "2": "Sales Order",
                        "3": "Parts Order",
                      };
                      return labels[String(selected)] ?? String(selected);
                    },
                  }}
                >
                  <MenuItem value="">{"\u200b"}</MenuItem>
                  <MenuItem value="1">Work Order</MenuItem>
                  <MenuItem value="2">Sales Order</MenuItem>
                  <MenuItem value="3">Parts Order</MenuItem>
                </Field>
                <br />
              </>
            )}
            <Field
              autoFocus
              required
              fullWidth
              component={CompanyAutocomplete}
              companyType={OrderType.Customer}
              label="Bill To"
              name="deliver"
            />
            <Field
              fullWidth
              component={TextField}
              type="text"
              label={quote ? "Sales Order Name" : "Work Order Name"}
              name="name"
            />
            {/* {jobConfig?.status ? (
              <>
                <Field
                  component={Select}
                  id="status"
                  name="status_id"
                  label="Status"
                >
                  {Object.entries(jobConfig.status).map(([key, value]) => (
                    <MenuItem value={key} key={key}>
                      {value as unknown as string}
                    </MenuItem>
                  ))}
                </Field>
                <br />
              </>
            ) : (
              <></>
            )} */}
            {/* <Field
              component={Select}
              id="to_stock"
              name="to_stock"
              label="Closing Type"
            >
              {toStockOptions.map((option) => (
                <MenuItem value={option.id} key={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Field>
            <br /> */}
            <Field
              component={Select}
              id="division"
              name="division"
              label="Division"
            >
              {divisions.map((division) => (
                <MenuItem value={division.id} key={division.id}>
                  {division.name}
                </MenuItem>
              ))}
            </Field>
            <br />
            
            {/* <br />
            <Field
              component={Select}
              id="aircraft_id"
              name="aircraft_id"
              label="Aircraft Registration"
              displayEmpty
            >
              <MenuItem value="0">
                <em></em>
              </MenuItem>
              {aircraft?.length && aircraft.map((a) => (
                <MenuItem value={a.id} key={a.id}>
                  {a.registration}
                </MenuItem>
              ))}
            </Field> */}
          <br />
          { !isAutoCreateJob &&
            <Box
              flex="0 0 auto"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              p={1}
            >
              <CancelButton onClick={() => setValue("")} />
              <Button
                disabled={isSubmitting}
                // @ts-ignore
                onClick={(event) => handleSubmit(event)}
              >
                Add
              </Button>
            </Box>
          }
        </div>
      )}
    </Formik>
  );
};
